import { Project, PageSubmission } from '@/models/Project';
import { Dictionary } from 'lodash';
import { ContactExtFieldMapping, ContactFieldMapping } from './Contacts';
import { Newsletter } from './Newsletter';
import * as RefData from './RefData';
import SelectOption, { SelectOptionWithData } from './SelectOption';
import SmsTemplateObject from './SmsTemplateObject';
import * as Enums from '@/models/enums';

export interface LoginResponse {
    Success: boolean;
    Key: string;
}
export enum ExportType {
    Default = 'Default',
    EmailDetail = 'EmailDetail',
    SMSDetail = 'SMSDetail',
    Contacts = 'Contacts',
}
export class ExportStatusObject {
    Id: string;
    Type: ExportType;
    State: string = '';
    ByUser: number;
    ReportUrl?: string | null;
    Message?: string | null;

    constructor(id: string, type: ExportType, byUser: number) {
        this.Id = id;
        this.Type = type;
        this.Type = type;
        this.Message = null;
        this.ReportUrl = null;
        this.ByUser = byUser;
    }

    public Key() {
        return `Export-${this.ByUser}--${this.Type}-${this.Id}`;
    }

    public UpdateStorage() {
        const key = this.Key();
        localStorage.setItem(key, JSON.stringify(this));
    }
}

export class GeneralState {
    CurrentDate: Date = new Date();
    IsAuthenticated: boolean = false;
    Lang: string = '';
    UserFullName: string = '';
    BuildVersion?: number;
    Pages: Project[] = [];
    Newsletters: Newsletter[] = [];
    UserId: number = 0;
    CurrentPageId: string = '';
    CurrentRouteName: string = '';
    ImportSwaggerDataStructures: ImportSwaggerDataStructure[] = [];
    BlobEndPoint: string = '';
    GeneralContactFields: SelectOptionWithData<string>[] = [];
    AllContactFields: ContactFieldMapping[] = [];

    //RefData
    EmailProfiles: SelectOptionWithData<RefData.EmailProfileObject>[] = [];
    SMSProfiles: SelectOptionWithData<RefData.SMSProfileObject>[] = [];
    Groups: SelectOption[] = [];
    Automations: SelectOption[] = [];
    Countries: SelectOption[] = [];
    SmsTemplates: SmsTemplateObject[] = [];
    Tags: SelectOption[] = [];
    CustomListsValues: SelectOptionWithData<RefData.CustomListValueObject>[] = [];

    ExportJobs: ExportStatusObject[] = [];
    // translations
    Genders: SelectOption[] = [];
    FieldTypes: SelectOption[] = [];
    BatchStatus: SelectOption[] = [];
    FilterOperation_DependsOnFieldType: { [key in Enums.ContactFilterTypeEnum]: SelectOption[] } | null = null;
}
export interface UserState {
    Token: string;
}

export interface Template {
    Name: string;
    Html: string;
    Components: string;
    Assets: string;
    Css: string;
    Styles: string;
    Direction: string;
}

export interface StartTemplate {
    Id: number;
    Name: string;
    Html: string;
    Components: string;
    Assets: string;
    Css: string;
    Styles: string;
    Direction: string;
    Type: ProjectType;
}

export enum DataType {
    Text = 1,
    Select = 2,
    CheckBox = 3,
    RadioButton = 4,
    TextArea = 5,
    Number = 6,
    Date = 7,
    Button = 8,
    DateTime = 9,
    Signature = 10,
    Toggle = 11,
}

export enum DataFormat {
    Button = 1,
    Link = 2,
    DDMMYYYY = 3,
    MMDDYYYY = 4,
    NumberFormated = 5,
    NumberNotFortmated = 6,
    Currency = 7,
    PhoneNumber = 8,
}

export enum PageStatus {
    NotPublished = 0,
    Published = 1,
    OnConstruction = 2,
}

export enum ActionType {
    Update = 0,
    Delete = 1,
}

export enum PostDataType {
    Form = 0,
    CRM = 1,
    Function = 2,
    Url = 3,
}

export enum PostTypeAction {
    PostAction = 0,
    PreAction = 1,
}

export enum SubmissionStatus {
    None = 0,
    Success = 1,
    InvalidRequired = 2,
    InvalidApi = 3,
    InvalidTag = 4,
    InvalidCaptcha = 5,
    Invalid = 99,
}

export enum FormFieldChangedType {
    None = 0,
    Added = 1,
    Removed = 2,
    Changed = 3,
    OptionsChanged = 4,
    OptionsAdded = 5,
    OptionsRemoved = 6,
}

export interface ImportSwaggerDataStructure {
    Id: string;
    Path: string;
    Name: string;
    ExtSysName: string;
    AuthBearerToken: string;
    UserId: number;
}

export interface InitResponse {
    Lang: string;
    Pages: Project[];
    Newsletters: Newsletter[];
    IsAuthenticated: boolean;
    UserFullName: string;
    UserId: number;
    ImportSwaggerDataStructures: ImportSwaggerDataStructure[];
    BlobEndPoint: string;
    ContactFields: ContactFieldMapping[];
    EmailProfiles: RefData.EmailProfileObject[];
    SMSProfiles: RefData.SMSProfileObject[];
    Automations: IdNameObject[];
    Groups: IdNameObject[];
    Countries: IdNameObject[];
    SmsTemplates: SmsTemplateObject[];
    Tags: IdNameObject[];
    CustomListsValues: RefData.CustomListValueObject[];
    BuildVersion: number;
}
export interface ChangeLanguage {
    Lang: string;
}
export interface StoreInitData {
    UserName: string;
    UserId: number;
    Pages: Project[];
}

export interface UpdatePageProperties {
    PageId: string;
    Properties: PageProperty[];
}

export interface PageProperty {
    Property: string;
    Value: unknown;
}

export interface ViewPageSubmission extends PageSubmission {
    Payload: Dictionary<unknown> | null;
    UrlParams: Dictionary<unknown> | null;
}

export interface PageSubmitResponse {
    Status: SubmissionStatus;
    Redirect: string;
}

export interface PagePublishResponse {
    Link: string;
    ClearCacheResult: ClearCacheResult;
}

export enum ClearCacheResult {
    None = 0,
    Accepted = 1,
    Error = 2,
}

export interface ModalEvent {
    cancel: Event;
    params: Record<string, any>;
}
export interface ModalBeforeOpenEvent {
    ref: ModalEvent;
    params: any;
    cancel: () => void;
}
export interface ModalBeforeOpenEventTyped<PARAM> {
    ref: ModalEvent;
    params: PARAM;
    cancel: () => void;
}

export enum ProjectType {
    None = 0,
    Landing = 1,
    Form = 2,
    Newsletter = 3,
    Mail = 4,
}

export interface ContactFieldsMappingResponse {
    Mapping: ContactFieldMapping[];
}

export interface ContactExtFieldsMappingResponse {
    Mapping: ContactExtFieldMapping[];
}

export interface SaveContactFieldsMappingRequest {
    Mapping: ContactFieldMapping[];
    ToDelete: string[];
}
export interface ExportStatus {
    State: string;
    ReportUrl: string | null;
}
export interface ImportStatus {
    State: string;
    Mapping: string[] | null;
}
export interface UploadExcelImportContacts {
    TaskId: string;
    Success: boolean;
}
export interface IdNameObject {
    Id: number;
    Name: string;
}

export interface IdTextObject {
    Id: string;
    Text: string;
}

export enum InputType {
    Text = 1,
    Number = 2,
    Email = 3,
    IL_PID = 4,
    Digits = 5,
}
export enum SmsTemplatesSortTypes {
    Name = 1,
    Date = 2,
    SentQty = 3,
}
